import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyBrand } from "@/lib/site";
import { isAccountant as canAccount, isManager as canManage, isAdmin as canAdmin, roleLabel } from "@/lib/roles";
import { kes } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, KeyRound } from "lucide-react";

const FUEL_KINDS = ["cash", "mobile_money", "card", "app", "invoice", "coupon", "other"];

export default function Config() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [prices, setPrices] = useState([]);
  const canEdit = canManage(user) || canAccount(user);

  const load = async () => {
    const [{ data }, pr] = await Promise.all([api.get("/config"), api.get("/config/fuel-prices")]);
    setCfg(data);
    setPrices(pr.data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!cfg) return <div className="py-20 text-center text-muted-foreground">Loading configuration…</div>;

  const productName = (id) => cfg.products.find((p) => p.id === id)?.name || "—";
  const tankLabel = (id) => cfg.tanks.find((t) => t.id === id)?.label || "—";

  const create = async (coll, body) => {
    try {
      await api.post(`/config/${coll}`, body);
      toast.success("Added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const update = async (coll, id, body) => {
    try {
      await api.put(`/config/${coll}/${id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const retire = async (coll, id) => {
    try {
      await api.delete(`/config/${coll}/${id}`);
      toast.success("Retired");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Station Setup</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure pumps, tanks, products, non-fuel items, payment channels &amp; prices</p>
      </div>

      <Tabs defaultValue="station">
        <TabsList className="flex-wrap">
          <TabsTrigger value="station" data-testid="tab-station">Station</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="tanks" data-testid="tab-tanks">Tanks</TabsTrigger>
          <TabsTrigger value="pumps" data-testid="tab-pumps">Pumps</TabsTrigger>
          <TabsTrigger value="nonfuel" data-testid="tab-nonfuel">Non-Fuel</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="attendants" data-testid="tab-attendants">Attendants</TabsTrigger>
          <TabsTrigger value="tills" data-testid="tab-tills">M-Pesa Tills</TabsTrigger>
          <TabsTrigger value="prices" data-testid="tab-prices">Fuel Prices</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Customers &amp; Accounts</TabsTrigger>
          <TabsTrigger value="expense-categories" data-testid="tab-expense-categories">Expense Categories</TabsTrigger>
          
          {canAdmin(user) && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <SimpleCrud
            title="Fuel Products"
            items={cfg.products}
            columns={[{ k: "name", label: "Name" }, { k: "unit", label: "Unit" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("products", id)}
            addFields={[{ k: "name", label: "Name", required: true }, { k: "unit", label: "Unit", default: "L" }]}
            onAdd={(b) => create("products", { ...b, kind: "fuel" })}
          />
        </TabsContent>

        {/* Tanks */}
        <TabsContent value="tanks">
          <SimpleCrud
            title="Underground Tanks"
            items={cfg.tanks.map((t) => ({ ...t, product: productName(t.product_id) }))}
            columns={[{ k: "label", label: "Tank" }, { k: "product", label: "Product" }, { k: "capacity", label: "Capacity (L)" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("tanks", id)}
            addFields={[
              { k: "label", label: "Label", required: true },
              { k: "product_id", label: "Product", type: "select", options: cfg.products.map((p) => ({ value: p.id, label: p.name })) },
              { k: "capacity", label: "Capacity (L)", type: "number" },
            ]}
            onAdd={(b) => create("tanks", b)}
          />
        </TabsContent>

        {/* Pumps */}
        <TabsContent value="pumps">
          <SimpleCrud
            title="Pumps by Island"
            items={cfg.pumps.map((p) => ({ ...p, product: productName(p.product_id), tank: tankLabel(p.tank_id) }))}
            columns={[{ k: "island", label: "Island" }, { k: "label", label: "Pump" }, { k: "product", label: "Product" }, { k: "tank", label: "Tank" }, { k: "attendant", label: "Attendant" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("pumps", id)}
            addFields={[
              { k: "island", label: "Island", required: true },
              { k: "label", label: "Pump Label", required: true },
              { k: "product_id", label: "Product", type: "select", options: cfg.products.map((p) => ({ value: p.id, label: p.name })) },
              { k: "tank_id", label: "Tank", type: "select", options: cfg.tanks.map((t) => ({ value: t.id, label: t.label })) },
              { k: "attendant", label: "Attendant" },
            ]}
            onAdd={(b) => create("pumps", b)}
          />
        </TabsContent>

        {/* Non-fuel */}
        <TabsContent value="nonfuel">
          <SimpleCrud
            title="Lubes, LPG & Accessories"
            items={cfg.nonfuel_items}
            columns={[{ k: "category", label: "Category" }, { k: "name", label: "Item" }, { k: "unit_price", label: "Unit Price", money: true }, { k: "cost_price", label: "Cost Price", money: true }]}
            canEdit={canEdit}
            onRetire={(id) => retire("nonfuel_items", id)}
            addFields={[
              { k: "category", label: "Category", type: "select", options: [{ value: "lube", label: "Lube" }, { value: "lpg", label: "LPG" }, { value: "accessory", label: "Accessory" }] },
              { k: "name", label: "Name", required: true },
              { k: "unit_price", label: "Unit Price (KES)", type: "number" },
              { k: "cost_price", label: "Cost Price (KES)", type: "number" },
            ]}
            onAdd={(b) => create("nonfuel_items", b)}
          />
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments">
          <SimpleCrud
            title="Payment Channels"
            items={cfg.payment_methods}
            columns={[{ k: "name", label: "Method" }, { k: "kind", label: "Type" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("payment_methods", id)}
            addFields={[
              { k: "name", label: "Name", required: true },
              { k: "kind", label: "Type", type: "select", options: FUEL_KINDS.map((k) => ({ value: k, label: k })) },
            ]}
            onAdd={(b) => create("payment_methods", b)}
          />
        </TabsContent>

        {/* Attendants */}
        <TabsContent value="attendants">
          <SimpleCrud
            title="Pump Attendants"
            items={cfg.attendants || []}
            columns={[{ k: "name", label: "Name" }, { k: "phone", label: "Phone" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("attendants", id)}
            addFields={[{ k: "name", label: "Name", required: true }, { k: "phone", label: "Phone" }]}
            onAdd={(b) => create("attendants", b)}
          />
        </TabsContent>

        {/* M-Pesa Tills */}
        <TabsContent value="tills">
          <SimpleCrud
            title="M-Pesa Tills"
            items={cfg.tills || []}
            columns={[{ k: "label", label: "Label" }, { k: "till_number", label: "Till Number" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("tills", id)}
            addFields={[{ k: "label", label: "Label (e.g. Till 1)", required: true }, { k: "till_number", label: "Till Number", required: true }]}
            onAdd={(b) => create("tills", b)}
          />
        </TabsContent>

        {/* Fuel prices */}
        <TabsContent value="prices">
          <FuelPrices cfg={cfg} prices={prices} canEdit={canEdit} reload={load} />
        </TabsContent>

        {/* Debtors: credit customers (picked on each shift's invoice lines) and card schemes that take a
            payment method's whole column (Card POS, Rubis Card) - balances under Accounting */}
        <TabsContent value="accounts">
          <SimpleCrud
            title="Customers & Accounts (debtors)"
            items={(cfg.receivable_accounts || []).map((a) => ({
              ...a, kind_label: a.kind === "card" ? "Card scheme" : "Credit customer",
              method_name: cfg.payment_methods.find((m) => m.id === a.method_id)?.name || "— (customer invoices)",
            }))}
            columns={[
              { k: "name", label: "Name" }, { k: "kind_label", label: "Type" }, { k: "method_name", label: "Takes the whole column" },
              { k: "phone", label: "Phone", editable: true },
              { k: "credit_limit", label: "Credit limit (KES)", editable: true, placeholder: "0 = none" },
              { k: "opening_balance", label: "Opening balance owed (KES)", editable: true, placeholder: "0" },
            ]}
            canEdit={canEdit}
            onRetire={(id) => retire("receivable_accounts", id)}
            onUpdate={(id, body) => update("receivable_accounts", id, body)}
            addFields={[
              { k: "name", label: "Name", required: true },
              { k: "kind", label: "Type", type: "select", options: [{ value: "customer", label: "Credit customer" }, { value: "card", label: "Card scheme (e.g. Card POS, Rubis Card)" }] },
              { k: "method_id", label: "Card scheme only: payment column it takes", type: "select",
                options: cfg.payment_methods.filter((m) => m.active !== false && !["cash", "mobile_money", "invoice"].includes(m.kind)).map((m) => ({ value: m.id, label: m.name })) },
              { k: "phone", label: "Phone" },
              { k: "credit_limit", label: "Credit limit (KES, 0 = none)", type: "number" },
              { k: "opening_balance", label: "Opening balance owed today (KES)", type: "number" },
            ]}
            onAdd={(b) => create("receivable_accounts", { kind: "customer", ...b, ...(b.kind === "card" ? {} : { method_id: null }) })}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Credit customers appear in each shift&apos;s Invoices list. A card scheme takes its payment column from every
            attendant automatically (e.g. Card POS, Rubis Card) and is cleared when the settlement is recorded under Accounting.
          </p>
        </TabsContent>

        {/* Expense categories — for Section E per-shift expenses */}
        <TabsContent value="expense-categories">
          <SimpleCrud
            title="Expense Categories"
            items={cfg.expense_categories || []}
            columns={[{ k: "label", label: "Label" }]}
            canEdit={canEdit}
            onRetire={(id) => retire("expense_categories", id)}
            addFields={[{ k: "label", label: "Category Label (e.g. Utilities)", required: true }]}
            onAdd={(b) => create("expense_categories", b)}
          />
        </TabsContent>

        {/* Station tolerances */}
        <TabsContent value="station">
          <div className="flex flex-wrap gap-6">
            <StationIdentity station={cfg.station} canEdit={canEdit} reload={load} />
            <EmailSettings station={cfg.station} isAccountant={canAccount(user)} reload={load} />
            {canAccount(user) && <Letterhead station={cfg.station} reload={load} />}
            {canAdmin(user) && <SiteColour station={cfg.station} reload={load} />}
          </div>
          <StationSettings station={cfg.station} canEdit={canEdit} reload={load} />
        </TabsContent>

        {/* User accounts */}
        {canAdmin(user) && (
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function AddDialog({ title, fields, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const submit = async () => {
    await onAdd(form);
    setForm({});
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-brand/90" data-testid="config-add-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{`Add — ${title}`}</DialogTitle><DialogDescription>Fill in the fields and save.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          {fields.map((f) => (
            <div key={f.k} className="space-y-1.5">
              <Label>{f.label}</Label>
              {f.type === "select" ? (
                <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                  <SelectTrigger data-testid={`config-field-${f.k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.type === "number" ? "number" : "text"}
                  value={form[f.k] ?? f.default ?? ""}
                  onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                  data-testid={`config-field-${f.k}`}
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} className="bg-brand hover:bg-brand/90" data-testid="config-add-confirm">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Columns marked `editable` render an inline input that saves (onUpdate) when it loses focus.
function SimpleCrud({ title, items, columns, addFields, onAdd, onRetire, onUpdate, canEdit }) {
  return (
    <Card className="mt-4 overflow-hidden">
      <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-3">
        <h3 className="font-display font-semibold text-slate-800">{title}</h3>
        {canEdit && <AddDialog title={title} fields={addFields} onAdd={onAdd} />}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c.k}>{c.label}</TableHead>)}
            <TableHead className="text-right">Status</TableHead>
            {canEdit && <TableHead className="w-12" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} className={it.active === false ? "opacity-50" : ""} data-testid={`config-row-${it.id}`}>
              {columns.map((c) => (
                <TableCell key={c.k} className={c.money ? "font-mono-fin" : ""}>
                  {c.editable && canEdit && onUpdate ? (
                    <Input
                      className="h-8 max-w-[12rem]" defaultValue={it[c.k] ?? ""} placeholder={c.placeholder}
                      data-testid={`config-edit-${c.k}-${it.id}`}
                      onBlur={(e) => { if (e.target.value !== String(it[c.k] ?? "")) onUpdate(it.id, { [c.k]: e.target.value.trim() }); }}
                    />
                  ) : c.money ? kes(it[c.k]) : (it[c.k] ?? "—")}
                </TableCell>
              ))}
              <TableCell className="text-right">
                {it.active === false ? <Badge variant="secondary">Retired</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>}
              </TableCell>
              {canEdit && (
                <TableCell className="text-right">
                  {it.active !== false && (
                    <Button variant="ghost" size="icon" onClick={() => onRetire(it.id)} data-testid={`config-retire-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Letterhead({ station, reload }) {
  const keys = ["letterhead_address", "letterhead_phone", "letterhead_email", "letterhead_pin"];
  const [form, setForm] = useState(Object.fromEntries(keys.map((k) => [k, station[k] || ""])));
  const save = async () => {
    try {
      await api.put("/config/station", form);
      toast.success("Letterhead saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const field = (k, label, placeholder) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[k]} placeholder={placeholder} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`lh-${k}`} />
    </div>
  );
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Letterhead (customer statements)</h3>
      <p className="mt-1 text-xs text-muted-foreground">Printed under {station.operator || "the company name"} on statements. Leave blank what you don&apos;t want shown.</p>
      <div className="mt-4 grid gap-3">
        {field("letterhead_address", "Postal / physical address", "e.g. P.O. Box 12345-00100, Nairobi")}
        <div className="grid grid-cols-2 gap-3">
          {field("letterhead_phone", "Phone", "e.g. 0722 000 000")}
          {field("letterhead_email", "Email", "e.g. accounts@…")}
        </div>
        {field("letterhead_pin", "KRA PIN", "e.g. P051234567X")}
        <Button onClick={save} className="w-fit bg-brand hover:bg-brand/90" data-testid="lh-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
      </div>
    </Card>
  );
}

function FuelPrices({ cfg, prices, canEdit, reload }) {
  const { user } = useAuth();
  const isAccountant = canAccount(user);
  const [productId, setProductId] = useState(cfg.products[0]?.id || "");
  const [price, setPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const add = async () => {
    try {
      await api.post("/config/fuel-prices", {
        product_id: productId, unit_price: Number(price),
        ...(isAccountant && costPrice !== "" ? { cost_price: Number(costPrice) } : {}),
      });
      toast.success("Price version recorded");
      setPrice("");
      setCostPrice("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Card className="mt-4 overflow-hidden">
      <div className="border-b bg-slate-50 px-4 py-3">
        <h3 className="font-display font-semibold text-slate-800">Versioned Fuel Prices</h3>
        <p className="text-xs text-muted-foreground">Prices are versioned — new prices are added, never overwritten.</p>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 border-b bg-white px-4 py-3">
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-48" data-testid="price-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cfg.products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Selling Price / Litre (KES)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="w-40" data-testid="price-value-input" />
          </div>
          {isAccountant && (
            <div className="space-y-1.5">
              <Label>Cost Price / Litre (KES)</Label>
              <Input type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="w-40" data-testid="price-cost-input" />
            </div>
          )}
          <Button onClick={add} className="bg-brand hover:bg-brand/90" data-testid="price-add-button"><Save className="mr-1.5 h-4 w-4" />Record</Button>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Product</TableHead><TableHead className="text-right">Selling Price/L</TableHead>
            {isAccountant && <TableHead className="text-right">Cost Price/L</TableHead>}
            <TableHead>Effective</TableHead><TableHead>By</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{cfg.products.find((x) => x.id === p.product_id)?.name || "—"}</TableCell>
              <TableCell className="text-right font-mono-fin">{kes(p.unit_price)}</TableCell>
              {isAccountant && <TableCell className="text-right font-mono-fin">{p.cost_price != null ? kes(p.cost_price) : "—"}</TableCell>}
              <TableCell className="text-xs text-muted-foreground">{(p.effective_from || "").slice(0, 10)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.created_by}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function StationIdentity({ station, canEdit, reload }) {
  const [form, setForm] = useState({ name: station.name || "", operator: station.operator || "", city: station.city || "" });
  const save = async () => {
    try {
      await api.put("/config/station", form);
      toast.success("Station details saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Station</h3>
      <p className="mt-1 text-xs text-muted-foreground">Shown on the login page, the menu bar, the shift report and the emails.</p>
      <div className="mt-4 space-y-4">
        {[["name", "Station name"], ["operator", "Company"], ["city", "Town"]].map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={`st-${k}`}>{label}</Label>
            <Input id={`st-${k}`} value={form[k]} disabled={!canEdit} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`station-${k}-input`} />
          </div>
        ))}
        {canEdit && <Button onClick={save} className="bg-brand hover:bg-brand/90" data-testid="station-save-button"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
      </div>
    </Card>
  );
}

function SiteColour({ station, reload }) {
  const [colour, setColour] = useState(station.brand_color || "#800000");
  const save = async () => {
    try {
      await api.put("/config/station", { brand_color: colour });
      applyBrand(colour);
      toast.success("Site colour saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Site colour</h3>
      <p className="mt-1 text-xs text-muted-foreground">The top bar and buttons use this colour, so each site looks different. Only the Admin can change it.</p>
      <div className="mt-4 flex items-center gap-3">
        <input type="color" value={colour} onChange={(e) => setColour(e.target.value.toUpperCase())} className="h-10 w-14 cursor-pointer rounded border" data-testid="site-colour-input" />
        <span className="font-mono text-sm">{colour}</span>
        <Button onClick={save} className="bg-brand hover:bg-brand/90" data-testid="site-colour-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
      </div>
    </Card>
  );
}

function EmailSettings({ station, isAccountant, reload }) {
  const [text, setText] = useState((station.notify_emails || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const loadLog = useCallback(async () => {
    if (!isAccountant) return;
    try { setLog((await api.get("/config/email-log")).data); } catch { setLog([]); }
  }, [isAccountant]);
  useEffect(() => { loadLog(); }, [loadLog]);
  const emails = text.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/config/station", { notify_emails: emails });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/config/test-email");
      toast.success(`Test email sent to ${data.sent_to.join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      loadLog();
    }
  };
  const STATUS = { sent: "text-emerald-700", failed: "text-rose-600", not_configured: "text-amber-700" };
  const KIND = { shift_summary: "Shift summary", monthly_reports: "Monthly reports", alert: "Alert", password_reset: "Password reset", test: "Test" };
  return (
    <Card className="mt-4 max-w-lg p-6">
      <h3 className="font-display font-semibold text-slate-800">Email notifications</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Every submitted shift (summary and PDF report) goes to all Accountants and Admins, and also to these addresses
        (one per line). Only the accountant can change this.
      </p>
      <textarea className="mt-3 h-24 w-full rounded-md border p-2 text-sm" disabled={!isAccountant} value={text}
        onChange={(e) => setText(e.target.value)} placeholder="name@example.com" data-testid="notify-emails-input" />
      {isAccountant && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand/90" data-testid="notify-save"><Save className="mr-1.5 h-4 w-4" />Save</Button>
          <Button variant="outline" onClick={test} disabled={busy} data-testid="notify-test">Send test email</Button>
        </div>
      )}
      {isAccountant && log && log.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-700">Recent emails</div>
          <div className="mt-1 max-h-48 overflow-y-auto text-xs">
            {log.map((e, i) => (
              <div key={i} className="flex flex-wrap gap-x-2 border-b py-1">
                <span className="text-muted-foreground">{String(e.at).slice(0, 16).replace("T", " ")}</span>
                <span className="font-medium">{KIND[e.kind] || e.kind}</span>
                <span className={STATUS[e.status] || ""}>{e.status === "not_configured" ? "email not set up" : e.status}</span>
                <span className="min-w-0 truncate text-muted-foreground" title={e.error || e.subject}>{e.error || e.subject}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function StationSettings({ station, canEdit, reload }) {
  const { user } = useAuth();
  const isAccountant = canAccount(user);
  const [form, setForm] = useState({
    cash_tolerance: station.cash_tolerance,
    throughput_warn_pct: station.throughput_warn_pct,
    throughput_crit_pct: station.throughput_crit_pct,
    cash_holding_alert_threshold: station.cash_holding_alert_threshold ?? 500000,
  });
  const [vatForm, setVatForm] = useState({
    fuel_vat_rate: station.fuel_vat_rate ?? 8,
    lube_vat_rate: station.lube_vat_rate ?? 16,
    accessory_vat_rate: station.accessory_vat_rate ?? 16,
    lpg_vat_rate: station.lpg_vat_rate ?? 0,
  });
  const save = async () => {
    try {
      await api.put("/config/station", form);
      toast.success("Tolerances saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const saveVat = async () => {
    try {
      await api.put("/config/station", vatForm);
      toast.success("VAT rates saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="flex flex-wrap gap-6">
      <Card className="mt-4 max-w-lg p-6">
        <h3 className="font-display font-semibold text-slate-800">Variance Tolerances</h3>
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Cash reconcile tolerance (KES)</Label>
            <Input type="number" value={form.cash_tolerance} disabled={!canEdit} onChange={(e) => setForm({ ...form, cash_tolerance: Number(e.target.value) })} data-testid="tol-cash-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Throughput warning threshold (%)</Label>
            <Input type="number" step="0.1" value={form.throughput_warn_pct} disabled={!canEdit} onChange={(e) => setForm({ ...form, throughput_warn_pct: Number(e.target.value) })} data-testid="tol-warn-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Throughput critical threshold (%)</Label>
            <Input type="number" step="0.1" value={form.throughput_crit_pct} disabled={!canEdit} onChange={(e) => setForm({ ...form, throughput_crit_pct: Number(e.target.value) })} data-testid="tol-crit-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Cash holding alert threshold (KES)</Label>
            <Input type="number" value={form.cash_holding_alert_threshold} disabled={!canEdit} onChange={(e) => setForm({ ...form, cash_holding_alert_threshold: Number(e.target.value) })} data-testid="tol-holding-input" />
            <p className="text-xs text-muted-foreground">Alerts on the Banking page once un-banked cash reaches this amount.</p>
          </div>
          {canEdit && <Button onClick={save} className="bg-brand hover:bg-brand/90" data-testid="tol-save-button"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
        </div>
      </Card>

      {isAccountant && (
        <Card className="mt-4 max-w-lg p-6">
          <h3 className="font-display font-semibold text-slate-800">VAT Rates</h3>
          <p className="mt-1 text-xs text-muted-foreground">Used by the Station P&L to strip VAT out of revenue and cost — prices in the system are VAT-inclusive.</p>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Fuel VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.fuel_vat_rate} onChange={(e) => setVatForm({ ...vatForm, fuel_vat_rate: Number(e.target.value) })} data-testid="vat-fuel-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Lube VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.lube_vat_rate} onChange={(e) => setVatForm({ ...vatForm, lube_vat_rate: Number(e.target.value) })} data-testid="vat-lube-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Accessory VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.accessory_vat_rate} onChange={(e) => setVatForm({ ...vatForm, accessory_vat_rate: Number(e.target.value) })} data-testid="vat-accessory-input" />
            </div>
            <div className="space-y-1.5">
              <Label>LPG VAT rate (%)</Label>
              <Input type="number" step="0.1" value={vatForm.lpg_vat_rate} onChange={(e) => setVatForm({ ...vatForm, lpg_vat_rate: Number(e.target.value) })} data-testid="vat-lpg-input" />
              <p className="text-xs text-muted-foreground">LPG is VAT-exempt by default (0%).</p>
            </div>
            <Button onClick={saveVat} className="bg-brand hover:bg-brand/90" data-testid="vat-save-button"><Save className="mr-1.5 h-4 w-4" />Save</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "manager" });
  const [busy, setBusy] = useState(false);
  const canManageUsers = canAccount(me);
  const meAdmin = canAdmin(me);

  const load = async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(typeof okMsg === "function" ? okMsg(res?.data) : okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const addUser = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await api.post("/auth/users", form);
      setForm({ name: "", email: "", role: "manager" });
      return r;
    }, (d) => (d?.invite_sent
      ? `${d.email} added — they've been emailed a link to set their password`
      : `${d?.email} added, but the email didn't send — use "Send set-password email" once email works`));
  };
  // admins are managed only by an admin; nobody changes their own role or disables themselves
  const canTouch = (u) => canManageUsers && u.id !== me?.id && (u.role !== "admin" || meAdmin);
  const roleOptions = meAdmin ? ["admin", "manager", "accountant"] : ["manager", "accountant"];

  if (!users) return <div className="py-10 text-center text-sm text-muted-foreground">Loading users…</div>;

  return (
    <div className="mt-4 grid max-w-3xl gap-4">
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-5 py-3">
          <h3 className="font-display font-semibold text-slate-800">User Accounts</h3>
          <p className="text-xs text-muted-foreground">
            Everyone signs in with their own account. The Admin can do everything; the Station Manager runs shifts, store
            deliveries and counts; the Accountant reconciles and approves. Only the Admin manages users. Forgot a password? Use &ldquo;Forgot password&rdquo;
            on the login page, or send a set-password email from here.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`} className={u.active === false ? "opacity-60" : ""}>
                <TableCell>{u.name}{u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  {canTouch(u) ? (
                    <select className="rounded border px-2 py-1 text-sm" disabled={busy} value={u.role}
                      onChange={(e) => run(() => api.patch(`/auth/users/${u.id}`, { role: e.target.value }), `${u.name} is now ${roleLabel(e.target.value)}`)}
                      data-testid={`role-select-${u.email}`}>
                      {[...new Set([...roleOptions, u.role])].map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                    </select>
                  ) : (
                    <Badge variant="secondary">{u.role_label || roleLabel(u.role)}</Badge>
                  )}
                </TableCell>
                <TableCell className={u.active === false ? "text-rose-600" : "text-emerald-700"}>{u.active === false ? "Disabled" : "Active"}</TableCell>
                <TableCell>
                  {canTouch(u) && (
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => run(() => api.post(`/auth/users/${u.id}/invite`), `Set-password email sent to ${u.email}`)}>
                        Send set-password email
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setTarget(u)} data-testid={`reset-pw-button-${u.email}`}>
                        <KeyRound className="mr-1.5 h-3.5 w-3.5" />Reset
                      </Button>
                      <Button variant="outline" size="sm" disabled={busy} className={u.active === false ? "" : "text-rose-600"}
                        onClick={() => run(() => api.patch(`/auth/users/${u.id}`, { active: u.active === false }),
                          u.active === false ? `${u.name} can sign in again` : `${u.name} is disabled and signed out`)}
                        data-testid={`toggle-active-${u.email}`}>
                        {u.active === false ? "Enable" : "Disable"}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {canManageUsers && (
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-800">Add a user</h3>
          <p className="mt-1 text-xs text-muted-foreground">They&apos;ll get an email with a link to set their own password (valid 48 hours). Nobody else ever knows it.{!meAdmin && " Only an Admin can add another Admin."}</p>
          <form onSubmit={addUser} className="mt-3 grid gap-3 sm:grid-cols-4 sm:items-end">
            <div className="space-y-1.5"><Label htmlFor="nu-name">Name</Label>
              <Input id="nu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="nu-email">Email</Label>
              <Input id="nu-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-1.5"><Label htmlFor="nu-role">Role</Label>
              <select id="nu-role" className="h-9 w-full rounded-md border px-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                {roleOptions.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select></div>
            <div className="sm:col-span-4"><Button type="submit" disabled={busy} className="bg-brand hover:bg-brand/90" data-testid="new-user-submit">Add user and send invitation</Button></div>
          </form>
        </Card>
      )}
      <ResetOtherPasswordDialog target={target} onOpenChange={(v) => !v && setTarget(null)} />
    </div>
  );
}

function ResetOtherPasswordDialog({ target, onOpenChange }) {
  const [ownPassword, setOwnPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOwnPassword(""); setNewPassword(""); setConfirmPassword("");
    onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/admin-reset-password", { user_id: target.id, own_password: ownPassword, new_password: newPassword });
      toast.success(`Password reset for ${target.email}`);
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password for {target?.name}</DialogTitle>
          <DialogDescription>Confirm with your own password to reset {target?.email}'s password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-own-password">Your Password</Label>
            <Input id="admin-own-password" type="password" value={ownPassword} autoComplete="current-password"
              onChange={(e) => setOwnPassword(e.target.value)} required data-testid="admin-reset-own-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-new-password">New Password for {target?.email}</Label>
            <Input id="admin-new-password" type="password" value={newPassword} autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="admin-reset-new-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-confirm-password">Confirm New Password</Label>
            <Input id="admin-confirm-password" type="password" value={confirmPassword} autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="admin-reset-confirm-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="admin-reset-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reset Password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
